import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Carrier } from '@app/services/carriers/carrier.model';
import { CarrierTypeNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { initRuleDisplayNameMap } from '@pages/checkout-app/configs';
import { internationalMethods, ShippingMethod } from '@pages/checkout-app/types/types';
import { CheckoutApiService } from '@pages/checkout-app/services';
import { BehaviorSubject, combineLatest, Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DeliveryMethodSection } from '@pages/checkout-app/checkout-layout/main/components/checkout/react/features/delivery-methods-list/sections/deliveryMethodDisplayOrder';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private onFormReset = new Subject<boolean>();
  readonly onFormReset$ = this.onFormReset.asObservable();

  private onSetSelectedCarrier = new BehaviorSubject<Carrier | null>(null);
  readonly onSetSelectedCarrier$ = this.onSetSelectedCarrier.asObservable();

  private initPrimaryLocationView = new BehaviorSubject<string>(null);
  readonly onInitPrimaryLocationView$ = this.initPrimaryLocationView.asObservable();

  private selectedFilterOptions = new BehaviorSubject<any>([]);
  readonly selectedFilterOptions$ = this.selectedFilterOptions.asObservable();

  private _shippingMethodCurrency = new BehaviorSubject(null);
  readonly shippingMethodCurrency$ = this._shippingMethodCurrency;

  initialShopSettingsSubject = new BehaviorSubject<any>('');

  setPolygonAddress = new Subject<string>();

  constructor(
    private checkoutApiService: CheckoutApiService,
    private router: Router,
  ) {
    initRuleDisplayNameMap();
  }

  loadDeliveryMethodSectionsOfCheckoutGroup(checkoutGroupId: number): Observable<DeliveryMethodSection[]> {
    return this.checkoutApiService.get(`admin/v1/shipping-methods-group/${checkoutGroupId}/delivery-method-sections`);
  }

  set shippingMethodCurrency(value: string) {
    this._shippingMethodCurrency.next(value);
  }

  setSelectedCarrier(carrier: Carrier) {
    this.onSetSelectedCarrier.next(carrier);
  }

  get selectedCarrier(): Carrier | null {
    return this.onSetSelectedCarrier.value;
  }

  private shippingMethodCategory = new BehaviorSubject<string | null>(null);

  setShippingMethodCategory(category: string | null) {
    this.shippingMethodCategory.next(category);
  }

  readonly isGlobaleMode$ = combineLatest([this.onSetSelectedCarrier$, this.shippingMethodCategory]).pipe(
    map(([carrier, category]) => this.computeGlobaleMode(carrier, category)),
  );

  get isGlobaleMode(): boolean {
    return this.computeGlobaleMode(this.onSetSelectedCarrier.value, this.shippingMethodCategory.value);
  }

  private computeGlobaleMode(carrier: Carrier | null, category: string | null): boolean {
    return (
      carrier?.type?.name === CarrierTypeNames.GLOBALE && internationalMethods.includes(category as ShippingMethod)
    );
  }

  setFormReset(value: boolean) {
    this.onFormReset.next(value);
  }

  setInitPrimaryLocationView(location: string) {
    this.initPrimaryLocationView.next(location);
  }

  setSelectedFilterOptions(value) {
    this.selectedFilterOptions.next(value);
  }

  isCheckoutEnabledOnEcommerce(): Observable<boolean> {
    return this.checkoutApiService.get('admin/v1/shippings/check/third-party-api').pipe(
      map((response: boolean) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }
}
